document.addEventListener('DOMContentLoaded', () => {
  const tsCommentsElement = document.getElementById('tscomments');

  const formatVariant = (value) => {
    const variant = String(value || '').trim();

    if (!variant.includes('/')) return variant;

    return variant.split('/').pop().trim();
  };

  /**
   * Brings a review into the shape the rest of this file works with.
   *
   * The current source is the app-owned metafield $app:trustedshops/product_reviews,
   * which uses named fields. Products the sync has not reached yet still deliver the
   * legacy positional array [, , rating, comment, customer, submitted_at, variant,
   * reply] — that branch can go once the old metafields are deleted.
   */
  const normalizeReview = (raw) => {
    if (Array.isArray(raw)) {
      const [, , rating = 0, comment = '', customer = '', date = '', variant = '', reply] = raw;

      return {
        rating: Number(rating) || 0,
        comment: comment || '',
        customer: customer || '',
        submitted_at: date || '',
        variant: formatVariant(variant),
        verified: true,
        reply: reply?.comment ? { comment: reply.comment, created_at: reply.createdAt } : null
      };
    }

    if (raw && typeof raw === 'object') {
      return {
        rating: Number(raw.rating) || 0,
        comment: raw.comment || '',
        customer: raw.customer || '',
        submitted_at: raw.submitted_at || '',
        variant: formatVariant(raw.variant),
        // product_reviews carries no `verified` flag, so this is false for every
        // review from it: claiming a verified purchase we cannot prove is the worse
        // error. The sliders keep the flag and their badge.
        verified: raw.verified === true,
        reply: raw.reply?.comment
          ? { comment: raw.reply.comment, created_at: raw.reply.created_at }
          : null
      };
    }

    return null;
  };

  try {
    const parsed = tsCommentsElement?.innerHTML.trim()
      ? JSON.parse(tsCommentsElement.innerHTML)
      : [];

    if (!Array.isArray(parsed)) {
      console.error('Kommentare sind kein Array:', parsed);
      window.tsComments = [];
    } else {
      window.tsComments = parsed.map(normalizeReview).filter(Boolean);
    }
  } catch (error) {
    console.error('Fehler beim Parsen der Kommentare:', error);
    window.tsComments = [];
  }

  const formatDate = (dateStr) => {
    if (!dateStr || typeof dateStr !== 'string') return '';
    try {
      const [year, month, day] = dateStr.split('T')[0].split('-');
      return `${day}.${month}.${year}`;
    } catch (error) {
      console.error('Fehler beim Formatieren des Datums:', error);
      return dateStr;
    }
  };

  /**
   * The variant title is merchant data from Shopify, but it goes through a template
   * literal into innerHTML, so it is escaped on principle. The fields around it are
   * older and interpolate raw — left as they are rather than changed in passing.
   */
  const escapeHtml = (value) =>
    String(value).replace(
      /[&<>"']/g,
      (character) =>
        ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[character]
    );

  const generateStarClass = (rating, starIndex) => {
    const full = rating >= starIndex;
    const half = rating >= starIndex - 0.5 && rating < starIndex;
    return `${half ? 'qlf-w-1/2' : ''} ${!full && !half ? 'qlf-w-full' : ''}`;
  };

  const verifiedBadge = `
    <span class="qlf-ts-comment-verified">
      <svg aria-hidden="true" viewBox="0 0 20 20"><path d="M7.7 14.3 3.4 10l1.4-1.4 2.9 2.9 7.5-7.5 1.4 1.4-8.9 8.9Z"/></svg>
      Verifizierter Kauf
    </span>`;

  const tsCommentTemplate = (data) => {
    if (!data || typeof data !== 'object') {
      console.error('Ungültiges Kommentar-Format:', data);
      return '';
    }

    try {
      const { rating, comment: text } = data;

      const username = data.customer || 'Anonymer Kunde';
      const commentDate = formatDate(data.submitted_at);
      const verified = data.verified ? verifiedBadge : '';

      // Which variant the review is about. Absent for products without variants and
      // for reviews whose SKU the sync could not match, so the markup is omitted
      // rather than left empty — an empty line under every comment reads as a bug.
      const variant = data.variant
        ? `<span class="qlf-ts-comment-variant">${escapeHtml(data.variant)}</span>`
        : '';

      const reply = data.reply
        ? `<button type="button" class="qlf-ts-support-message">Antwort von QLF anzeigen</button>
           <div class="qlf-ts-support-message-text" style="display: none">
             <strong>Quantum Leap Fitness</strong>
             <p>${escapeHtml(data.reply.comment)}</p>
           </div>`
        : '';

      const stars = [1, 2, 3, 4, 5]
        .map(
          (i) => `
            <span class="qlf-relative">
               <span class="${generateStarClass(rating, i)}" style="opacity: 0.65;background-color: #fff;position: absolute;top: 0;bottom: 0;right: 0;"></span>
               <svg class="qlf-w-4 qlf-h-4"><use href="#star" fill="#F6B812" /></svg>
            </span>`
        )
        .join('');

      return `
        <article class="qlf-ts-comment">
          <header class="qlf-ts-comment__header">
            <div class="qlf-ts-comment-rating__stars">${stars}</div>
            <time class="qlf-ts-comment__date">${commentDate}</time>
          </header>
          <p class="qlf-ts-comment__text">${escapeHtml(text)}</p>
          ${reply}
          <footer class="qlf-ts-comment__footer">
            <span class="qlf-ts-comment__customer">${escapeHtml(username)}</span>
            ${verified}
            ${variant}
          </footer>
        </article>`;
    } catch (error) {
      console.error('Fehler beim Rendern des Kommentars:', error);
      return '';
    }
  };

  const bindSupportMessageEvents = () => {
    try {
      document.querySelectorAll('.qlf-ts-support-message:not(.bound)').forEach((element) => {
        if (!element) return;

        element.classList.add('bound');
        element.addEventListener('click', function () {
          try {
            const messageText = this.nextElementSibling;
            if (messageText) {
              messageText.style.display = messageText.style.display === 'none' ? 'block' : 'none';
            }
          } catch (error) {
            console.error('Fehler beim Umschalten der Support-Nachricht:', error);
          }
        });
      });
    } catch (error) {
      console.error('Fehler beim Binden der Support-Nachricht-Events:', error);
    }
  };

  let currentSort = 'relevance';

  const selectComments = (reviews, filter = null, variantFilter = null) => {
    const timestamp = (review) => Date.parse(review.submitted_at) || 0;

    return reviews
      .filter((review) => {
        // An explicit star selection takes precedence over the relevance default.
        const matchesRating =
          filter !== null
            ? Math.round(review.rating) === filter
            : currentSort !== 'relevance' || review.rating === 4 || review.rating === 5;
        return matchesRating && (variantFilter === null || review.variant === variantFilter);
      })
      .sort((a, b) =>
        currentSort === 'oldest' ? timestamp(a) - timestamp(b) : timestamp(b) - timestamp(a)
      );
  };

  const getAllComments = (filter = null, variantFilter = null) => {
    try {
      const commentsContainer = document.querySelector('.ts-comments');
      const ratingsWrapper = document.querySelector('.ts-ratings-wrapper');
      const paginationContainer = document.querySelector('.ts-pagination');

      // Update visual indication of active filters
      const ratingFilterButton = document.querySelector('.qlf-ts-filter-open');
      const variantFilterButton = document.querySelector('.qlf-ts-variant-filter-open');

      // For rating filters
      let activeRatingFilter = null;
      document.querySelectorAll('.qlf-ts-filter-menu-item[data-filter]').forEach((el) => {
        const filterValue = el.getAttribute('data-filter');
        const isActive =
          (filterValue === 'all' && filter === null) ||
          (filterValue !== 'all' && filter === parseInt(filterValue));

        if (isActive) {
          el.classList.add('qlf-bg-gray-100');
          if (filterValue !== 'all') {
            const suffix = filterValue == 1 ? ' Stern' : ' Sterne';
            activeRatingFilter =
              el.querySelector('.qlf-flex') || el.querySelector('svg')
                ? filterValue + suffix
                : el.textContent.trim();
          }
        } else {
          el.classList.remove('qlf-bg-gray-100');
        }
      });

      // For variant filters
      let activeVariantFilter = null;
      document.querySelectorAll('[data-variant-filter]').forEach((el) => {
        const filterValue = el.getAttribute('data-variant-filter');
        const isActive =
          (filterValue === 'all' && variantFilter === null) ||
          (filterValue !== 'all' && variantFilter === filterValue);

        if (isActive) {
          el.classList.add('qlf-bg-gray-100');
          if (filterValue !== 'all') {
            activeVariantFilter = el.querySelector('span')
              ? el.querySelector('span').textContent.trim()
              : el.textContent.trim();
          }
        } else {
          el.classList.remove('qlf-bg-gray-100');
        }
      });

      // Update rating filter button text
      if (ratingFilterButton) {
        let ratingFilterText = currentSort === 'relevance' ? '4 & 5 Sterne' : 'Alle Bewertungen';

        if (activeRatingFilter) {
          ratingFilterText = activeRatingFilter;
        }

        // Update the button text, keeping the SVG icon
        const ratingFilterSvgIcon = ratingFilterButton.querySelector('svg');
        if (ratingFilterSvgIcon) {
          const ratingFilterSvgHtml = ratingFilterSvgIcon.outerHTML;
          ratingFilterButton.innerHTML = `${ratingFilterText} ${ratingFilterSvgHtml}`;
        }
      }

      // Update variant filter button text
      if (variantFilterButton) {
        let variantFilterText = 'Alle Varianten';

        if (activeVariantFilter) {
          variantFilterText = activeVariantFilter;
        }

        // Update the button text, keeping the SVG icon
        const variantFilterSvgIcon = variantFilterButton.querySelector('svg');
        if (variantFilterSvgIcon) {
          const variantFilterSvgHtml = variantFilterSvgIcon.outerHTML;
          variantFilterButton.innerHTML = `${variantFilterText} ${variantFilterSvgHtml}`;
        }
      }

      if (!commentsContainer || !ratingsWrapper || !paginationContainer) {
        console.warn('Erforderliche DOM-Elemente für Kommentare nicht gefunden');
        return;
      }

      const comments = selectComments(window.tsComments || [], filter, variantFilter);

      if (!window.tsComments?.length) {
        ratingsWrapper.style.display = 'none';
        return;
      }
      ratingsWrapper.style.display = '';

      if (comments.length === 0) {
        commentsContainer.innerHTML =
          '<p class="qlf-ts-comments-empty" role="status">Keine Bewertungen für diese Auswahl vorhanden.</p>';
        paginationContainer.innerHTML = '';
        return;
      }

      const itemsPerPage = 5;
      const totalPages = Math.ceil(comments.length / itemsPerPage);
      let currentPage = 1;

      const renderComments = (page) => {
        try {
          const start = (page - 1) * itemsPerPage;
          const end = start + itemsPerPage;
          const pageComments = comments.slice(start, end);

          commentsContainer.innerHTML = pageComments.map(tsCommentTemplate).join('');
          bindSupportMessageEvents();
          renderPagination();
        } catch (error) {
          console.error('Fehler beim Rendern der Kommentare:', error);
        }
      };

      const renderPagination = () => {
        try {
          let paginationHTML = `<div class="page-numbers nav-links">`;

          paginationHTML += `
                  <span class="prev">
                     <a href="#" data-page="${currentPage - 1}"${currentPage === 1 ? ' disabled' : ''}>
                        <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                           <path d="M6 0.818115L1 5.81812L6 10.8181" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"></path>
                        </svg>
                        <span class="visually-hidden">Vorherige Seite</span>
                     </a>
                  </span>`;

          paginationHTML += Array.from({ length: totalPages }, (_, i) => i + 1)
            .filter(
              (page) =>
                page === 1 ||
                page === totalPages ||
                (page >= currentPage - 1 && page <= currentPage + 3)
            )
            .map((page, index, array) => {
              let html = '';
              if (index > 0 && array[index - 1] !== page - 1) {
                html += `<span class="page">…</span>`;
              }
              html += `<span class="page${page === currentPage ? ' current' : ''}">${
                page === currentPage
                  ? page
                  : `<a href="#" data-page="${page}" title="Seite ${page}">${page}</a>`
              }</span>`;
              return html;
            })
            .join('');

          paginationHTML += `
                  <span class="next">
                     <a href="#" data-page="${currentPage + 1}"${currentPage === totalPages ? ' disabled' : ''}>
                        <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M1 0.818115L6 5.81812L1 10.8181" stroke="var(--color-body)" stroke-width="1.3" stroke-linecap="round" stroke-linejoin="round"></path>
                        </svg>
                        <span class="visually-hidden">Nächste Seite</span>
                     </a>
                  </span></div>`;

          paginationContainer.innerHTML = paginationHTML;
          paginationContainer.querySelectorAll('a[data-page]').forEach((button) => {
            if (button.hasAttribute('disabled')) return;

            button.addEventListener('click', (e) => {
              e.preventDefault();
              const pageNum = parseInt(button.dataset.page);
              if (isNaN(pageNum)) return;

              currentPage = pageNum;
              renderComments(currentPage);

              const reviewsSection = document.querySelector('#productreviews');
              if (reviewsSection) {
                reviewsSection.scrollIntoView({ behavior: 'smooth' });
              }
            });
          });
        } catch (error) {
          console.error('Fehler beim Rendern der Pagination:', error);
        }
      };

      renderComments(currentPage);
    } catch (error) {
      console.error('Fehler beim Laden der Kommentare:', error);
    }
  };

  const init = () => {
    try {
      // Rating filter elements
      const ratingFilterOpen = document.querySelector('.qlf-ts-filter-open:not(.bound)');
      const ratingFilterMenu = document.querySelector('.qlf-rating-filter-menu');
      const ratingFilterItems = document.querySelectorAll(
        '.qlf-ts-filter-menu-item[data-filter]:not(.bound)'
      );

      // Variant filter elements
      const variantFilterOpen = document.querySelector('.qlf-ts-variant-filter-open:not(.bound)');
      const variantFilterMenu = document.querySelector('.qlf-variant-filter-menu');
      const sortFilterOpen = document.querySelector('.qlf-ts-sort-open');
      const sortFilterMenu = document.querySelector('.qlf-sort-filter-menu');
      const closeSortMenu = () => {
        if (sortFilterMenu) sortFilterMenu.style.display = 'none';
        sortFilterOpen?.setAttribute('aria-expanded', 'false');
      };

      // Setup rating filter button
      if (ratingFilterOpen && ratingFilterMenu) {
        ratingFilterOpen.classList.add('bound');
        ratingFilterOpen.addEventListener('click', (e) => {
          e.stopPropagation();
          closeSortMenu();
          if (ratingFilterMenu.style.display === 'none') {
            // Close the variant filter menu if it's open
            if (variantFilterMenu && variantFilterMenu.style.display !== 'none') {
              variantFilterMenu.style.display = 'none';
            }

            // Show the menu
            ratingFilterMenu.style.display = 'block';

            // Adjust max-height based on visible items
            adjustFilterMenuHeight(ratingFilterMenu);
          } else {
            // Hide the menu
            ratingFilterMenu.style.display = 'none';
          }
        });
      }

      // Setup variant filter button
      if (variantFilterOpen && variantFilterMenu) {
        variantFilterOpen.classList.add('bound');
        variantFilterOpen.addEventListener('click', (e) => {
          e.stopPropagation();
          closeSortMenu();
          if (variantFilterMenu.style.display === 'none') {
            // Close the rating filter menu if it's open
            if (ratingFilterMenu && ratingFilterMenu.style.display !== 'none') {
              ratingFilterMenu.style.display = 'none';
            }

            // Show the menu
            variantFilterMenu.style.display = 'block';

            // Adjust max-height based on visible items
            adjustFilterMenuHeight(variantFilterMenu);
          } else {
            // Hide the menu
            variantFilterMenu.style.display = 'none';
          }
        });
      }

      const comments = window.tsComments || [];
      const filterCounts = {
        all: comments.length,
        1: comments.filter((c) => Math.round(c.rating) === 1).length,
        2: comments.filter((c) => Math.round(c.rating) === 2).length,
        3: comments.filter((c) => Math.round(c.rating) === 3).length,
        4: comments.filter((c) => Math.round(c.rating) === 4).length,
        5: comments.filter((c) => Math.round(c.rating) === 5).length
      };

      // Collect all unique variants
      const variants = new Set();
      comments.forEach((comment) => {
        if (comment.variant) {
          variants.add(comment.variant);
        }
      });

      // Add variant filters to the menu if there are any variants
      if (variants.size > 0) {
        const variantFiltersContainer = document.createElement('div');
        variantFiltersContainer.setAttribute('role', 'none');

        // Convert Set to Array and sort alphabetically
        const sortedVariants = Array.from(variants).sort((a, b) => a.localeCompare(b));

        // Add a filter option for each variant
        sortedVariants.forEach((variant) => {
          const variantCount = comments.filter((c) => c.variant === variant).length;

          const variantLink = document.createElement('a');
          variantLink.href = '#';
          variantLink.className =
            'qlf-ts-filter-menu-item qlf-text-gray-700 hover:qlf-bg-gray-100 qlf-flex qlf-justify-between qlf-items-center qlf-px-4 qlf-py-3 qlf-text-sm';
          variantLink.setAttribute('data-variant-filter', variant);
          variantLink.setAttribute('role', 'menuitem');
          variantLink.innerHTML = `<span>${variant}</span>`;

          if (variantCount === 0) {
            // Gray out variants with no ratings instead of hiding them
            variantLink.style.opacity = '0.5';
            variantLink.style.pointerEvents = 'none';
            variantLink.style.filter = 'grayscale(100%)';
          } else {
            // Reset styling for visible variants
            variantLink.style.opacity = '1';
            variantLink.style.pointerEvents = 'auto';
            variantLink.style.filter = 'none';
            variantLink.addEventListener('click', (e) => {
              e.preventDefault();
              // Remove active class from all variant filters
              document.querySelectorAll('[data-variant-filter]').forEach((el) => {
                el.classList.remove('qlf-bg-gray-100');
              });
              // Add active class to this filter
              variantLink.classList.add('qlf-bg-gray-100');
              currentVariantFilter = variant;
              // Update rating filters based on selected variant
              updateRatingFiltersAvailability(variant);
              getAllComments(currentRatingFilter, currentVariantFilter);
              variantFilterMenu.style.display = 'none';
            });
          }

          variantFiltersContainer.appendChild(variantLink);
        });

        // Add "Show all variants" option
        const showAllVariantsLink = document.createElement('a');
        showAllVariantsLink.href = '#';
        showAllVariantsLink.className =
          'qlf-ts-filter-menu-item qlf-border-t qlf-text-gray-700 hover:qlf-bg-gray-100 qlf-flex qlf-justify-between qlf-items-center qlf-px-4 qlf-py-3 qlf-text-sm';
        showAllVariantsLink.setAttribute('data-variant-filter', 'all');
        showAllVariantsLink.setAttribute('role', 'menuitem');
        showAllVariantsLink.innerHTML = '<span>Alle Varianten anzeigen</span>';

        showAllVariantsLink.addEventListener('click', (e) => {
          e.preventDefault();
          // Remove active class from all variant filters
          document.querySelectorAll('[data-variant-filter]').forEach((el) => {
            el.classList.remove('qlf-bg-gray-100');
          });
          // Add active class to this filter
          showAllVariantsLink.classList.add('qlf-bg-gray-100');
          currentVariantFilter = null;
          // Update rating filters to show all available ratings
          updateRatingFiltersAvailability(null);
          getAllComments(currentRatingFilter, null);
          variantFilterMenu.style.display = 'none';
        });

        variantFiltersContainer.appendChild(showAllVariantsLink);

        // Add the variant filters to the variant filter menu
        variantFilterMenu
          .querySelector('.variant-filters-container')
          .appendChild(variantFiltersContainer);
      } else {
        const variantFilterContainer = document.querySelector('.ts-variant-filter');
        if (variantFilterContainer) {
          variantFilterContainer.style.display = 'none';
        }
      }

      // Track current filters
      let currentRatingFilter = null;
      let currentVariantFilter = null;

      sortFilterOpen?.addEventListener('click', (event) => {
        event.stopPropagation();
        if (!sortFilterMenu) return;
        const open = sortFilterMenu.style.display === 'none';
        if (ratingFilterMenu) ratingFilterMenu.style.display = 'none';
        if (variantFilterMenu) variantFilterMenu.style.display = 'none';
        sortFilterMenu.style.display = open ? 'block' : 'none';
        sortFilterOpen.setAttribute('aria-expanded', String(open));
      });

      document.querySelectorAll('[data-sort]').forEach((item) => {
        item.addEventListener('click', (event) => {
          event.preventDefault();
          currentSort = item.dataset.sort;
          const label = sortFilterOpen?.querySelector('.qlf-ts-sort-label');
          if (label) label.textContent = item.textContent.trim();
          document.querySelectorAll('[data-sort]').forEach((option) => {
            const active = option === item;
            option.classList.toggle('qlf-bg-gray-100', active);
            option.setAttribute('aria-checked', String(active));
          });
          closeSortMenu();
          sortFilterOpen?.focus();
          getAllComments(currentRatingFilter, currentVariantFilter);
        });
      });

      document.addEventListener('keydown', (event) => {
        if (event.key === 'Escape' && sortFilterMenu?.style.display === 'block') {
          closeSortMenu();
          sortFilterOpen?.focus();
        }
      });

      // Helper function to adjust filter menu max-height based on visible items
      const adjustFilterMenuHeight = (menuElement) => {
        if (!menuElement) return;

        // Count visible and grayed-out items
        // We now consider items with opacity < 1 as "visually hidden" (grayed out)
        const grayedOutItems = Array.from(
          menuElement.querySelectorAll('[style*="opacity: 0.5"]')
        ).length;
        const totalItems = menuElement.querySelectorAll('a').length;
        const fullyVisibleItems = totalItems - grayedOutItems;

        // If there are few fully visible items, remove the fixed max-height
        if (fullyVisibleItems <= 5) {
          menuElement.style.maxHeight = 'none';
        } else {
          // Otherwise, use the original max-height
          menuElement.style.maxHeight = '60vh';
        }
      };

      // Function to update rating filter availability based on selected variant
      const updateRatingFiltersAvailability = (variantFilter) => {
        // Calculate available ratings for the current variant filter
        const availableRatings = {
          all: 0,
          1: 0,
          2: 0,
          3: 0,
          4: 0,
          5: 0
        };

        // Filter comments by variant if a variant filter is applied
        let filteredComments = comments;
        if (variantFilter !== null) {
          filteredComments = comments.filter((comment) => comment.variant === variantFilter);
        }

        // Count comments for each rating
        availableRatings.all = filteredComments.length;
        for (let i = 1; i <= 5; i++) {
          availableRatings[i] = filteredComments.filter((c) => Math.round(c.rating) === i).length;
        }

        // Update rating filter items - hide those that don't have comments with the selected variant
        document.querySelectorAll('.qlf-ts-filter-menu-item[data-filter]').forEach((item) => {
          const val = item.getAttribute('data-filter');

          if (
            (val === 'all' && availableRatings.all === 0) ||
            (val !== 'all' && availableRatings[val] === 0)
          ) {
            // Gray out rating filters that don't have comments with the selected variant
            item.style.opacity = '0.5';
            item.style.pointerEvents = 'none';
            item.style.filter = 'grayscale(100%)';
          } else {
            // Show rating filters that have comments
            item.style.display = '';
            // Reset styling for visible rating filters
            item.style.opacity = '1';
            item.style.pointerEvents = 'auto';
            item.style.filter = 'none';

            // Make sure the click event is bound only once
            if (!item.classList.contains('bound')) {
              item.classList.add('bound');
              item.addEventListener('click', (e) => {
                e.preventDefault();
                // Remove active class from all rating filters
                document.querySelectorAll('.qlf-ts-filter-menu-item[data-filter]').forEach((el) => {
                  el.classList.remove('qlf-bg-gray-100');
                });
                // Add active class to this filter
                item.classList.add('qlf-bg-gray-100');
                currentRatingFilter = val === 'all' ? null : parseInt(val);
                // Update variant filters based on selected rating
                updateVariantFiltersAvailability(currentRatingFilter);
                getAllComments(currentRatingFilter, currentVariantFilter);
                ratingFilterMenu.style.display = 'none';
              });
            }
          }
        });

        // Adjust filter menu height after updating filters
        adjustFilterMenuHeight(ratingFilterMenu);
      };

      // Function to update variant filter availability based on selected rating
      const updateVariantFiltersAvailability = (ratingFilter) => {
        // Calculate available variants for the current rating filter
        const availableVariants = new Set();

        // Filter comments by rating if a rating filter is applied
        let filteredComments = comments;
        if (ratingFilter !== null) {
          filteredComments = comments.filter(
            (comment) => Math.round(comment.rating) === ratingFilter
          );
        }

        // Collect all variants that have comments with the selected rating
        filteredComments.forEach((comment) => {
          if (comment.variant) {
            availableVariants.add(comment.variant);
          }
        });

        // Update variant filter items - hide those that don't have comments with the selected rating
        document.querySelectorAll('[data-variant-filter]').forEach((item) => {
          const val = item.getAttribute('data-variant-filter');

          if (val === 'all') {
            // "Show all variants" is always available
            item.style.display = '';
            // Reset styling for "Show all variants" option
            item.style.opacity = '1';
            item.style.pointerEvents = 'auto';
            item.style.filter = 'none';
          } else if (!availableVariants.has(val)) {
            // Gray out variants that don't have comments with the selected rating
            item.style.opacity = '0.5';
            item.style.pointerEvents = 'none';
            item.style.filter = 'grayscale(100%)';
          } else {
            // Show variants with comments
            item.style.display = '';
            // Reset styling for visible variants
            item.style.opacity = '1';
            item.style.pointerEvents = 'auto';
            item.style.filter = 'none';
          }
        });

        // If the currently selected variant is not available for this rating, reset to "all variants"
        if (currentVariantFilter !== null && !availableVariants.has(currentVariantFilter)) {
          currentVariantFilter = null;
          document.querySelectorAll('[data-variant-filter]').forEach((el) => {
            el.classList.remove('qlf-bg-gray-100');
          });
          const allVariantsFilter = document.querySelector('[data-variant-filter="all"]');
          if (allVariantsFilter) {
            allVariantsFilter.classList.add('qlf-bg-gray-100');
          }
        }

        // Adjust filter menu height after updating filters
        adjustFilterMenuHeight(variantFilterMenu);
      };

      // Initial setup of rating filters
      ratingFilterItems.forEach((item) => {
        if (!item) return;

        const val = item.getAttribute('data-filter');

        if (
          (val === 'all' && filterCounts.all === 0) ||
          (val !== 'all' && filterCounts[val] === 0)
        ) {
          // Gray out rating filters that don't have reviews
          item.style.opacity = '0.5';
          item.style.pointerEvents = 'none';
          item.style.filter = 'grayscale(100%)';
        } else {
          // Show rating filters that have reviews
          item.style.display = '';
          // Reset styling for visible rating filters
          item.style.opacity = '1';
          item.style.pointerEvents = 'auto';
          item.style.filter = 'none';
          item.classList.add('bound');
          item.addEventListener('click', (e) => {
            e.preventDefault();
            // Remove active class from all rating filters
            document.querySelectorAll('.qlf-ts-filter-menu-item[data-filter]').forEach((el) => {
              el.classList.remove('qlf-bg-gray-100');
            });
            // Add active class to this filter
            item.classList.add('qlf-bg-gray-100');
            currentRatingFilter = val === 'all' ? null : parseInt(val);
            // Update variant filters based on selected rating
            updateVariantFiltersAvailability(currentRatingFilter);
            getAllComments(currentRatingFilter, currentVariantFilter);
            ratingFilterMenu.style.display = 'none';
          });
        }
      });

      document.addEventListener('click', (e) => {
        if (!sortFilterOpen?.contains(e.target) && !sortFilterMenu?.contains(e.target)) {
          closeSortMenu();
        }
        // Close rating filter menu when clicking outside
        if (
          ratingFilterMenu &&
          ratingFilterMenu.style.display === 'block' &&
          ratingFilterOpen &&
          !ratingFilterOpen.contains(e.target) &&
          !ratingFilterMenu.contains(e.target)
        ) {
          ratingFilterMenu.style.display = 'none';
        }

        // Close variant filter menu when clicking outside
        if (
          variantFilterMenu &&
          variantFilterMenu.style.display === 'block' &&
          variantFilterOpen &&
          !variantFilterOpen.contains(e.target) &&
          !variantFilterMenu.contains(e.target)
        ) {
          variantFilterMenu.style.display = 'none';
        }
      });

      // Set default active filters
      const allRatingsFilter = document.querySelector(
        '.qlf-ts-filter-menu-item[data-filter="all"]'
      );
      if (allRatingsFilter) {
        allRatingsFilter.classList.add('qlf-bg-gray-100');
      }

      const allVariantsFilter = document.querySelector('[data-variant-filter="all"]');
      if (allVariantsFilter) {
        allVariantsFilter.classList.add('qlf-bg-gray-100');
      }

      // Initialize rating filters availability
      updateRatingFiltersAvailability(null);

      // Initialize variant filters availability
      updateVariantFiltersAvailability(null);

      getAllComments();
    } catch (error) {
      console.error('Fehler bei der Initialisierung der Kommentare:', error);
    }
  };

  /**
   * The section only inlines the first page of reviews — a product can have 2000,
   * and putting them all in the document would cost every visitor a megabyte for
   * something most of them never scroll to. The rest comes from
   * templates/product.ts-reviews.liquid.
   *
   * init() runs exactly once, and only on the complete list: it closes over the
   * review array (see `const comments` above) and appends the variant menu, so a
   * second run would work off stale data and duplicate the menu entries. Until it
   * runs, the inlined reviews are painted as a static preview — the block is never
   * empty and never jumps.
   */
  const setupLazyReviews = () => {
    const wrapper = document.querySelector('.ts-ratings-wrapper');
    if (!wrapper) return;

    const handles = (tsCommentsElement?.dataset.tsHandles || '')
      .split(',')
      .map((handle) => handle.trim())
      .filter(Boolean);
    const total = Number(tsCommentsElement?.dataset.tsTotal) || 0;
    const inlined = window.tsComments.length;

    // Nothing was held back, so there is nothing to wait for.
    if (!handles.length || total <= inlined) {
      init();
      return;
    }

    const commentsContainer = document.querySelector('.ts-comments');
    if (commentsContainer) {
      commentsContainer.innerHTML = selectComments(window.tsComments)
        .map(tsCommentTemplate)
        .join('');
      bindSupportMessageEvents();
    }

    let pending = null;
    let started = false;

    const fetchAll = () => {
      if (pending) return pending;

      pending = Promise.all(
        handles.map((handle) =>
          fetch(`/products/${encodeURIComponent(handle)}?view=ts-reviews`).then((response) => {
            if (!response.ok) {
              throw new Error(`${response.status} für ${handle}`);
            }
            return response.json();
          })
        )
      )
        .then((lists) => lists.flat().map(normalizeReview).filter(Boolean))
        .then((reviews) => {
          // getAllComments() hides the whole block on an empty list, so an empty
          // or broken response must not replace the reviews we already have.
          if (reviews.length < inlined) {
            throw new Error(`${reviews.length} Bewertungen erhalten, ${inlined} bereits vorhanden`);
          }
          return reviews;
        })
        .catch((error) => {
          // Drop the memo so a later interaction can try again; the preview stays.
          pending = null;
          started = false;
          throw error;
        });

      return pending;
    };

    const activate = () => {
      if (started) return;
      started = true;

      fetchAll()
        .then((reviews) => {
          window.tsComments = reviews;
          init();
        })
        .catch((error) => {
          console.error('Bewertungen konnten nicht nachgeladen werden:', error);
        });
    };

    // Load before the block is on screen, so filters and pagination are already
    // built by the time the reader can reach them.
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            activate();
          }
        },
        { rootMargin: '600px 0px' }
      );

      observer.observe(wrapper);
    } else {
      activate();
    }

    // Safety net for the case the observer never fires — the filter buttons are
    // inert until init() has run, so a click there must not be a dead end.
    ['.qlf-ts-filter-open', '.qlf-ts-variant-filter-open', '.qlf-ts-sort-open'].forEach(
      (selector) => {
        document.querySelector(selector)?.addEventListener('click', activate);
      }
    );
  };

  setupLazyReviews();
});
